import { Link } from "react-router-dom"
import type { CaseFileListRow } from "../contract"
import { lakh, shortDate } from "../answer/format"
import { isAppeal, matterPath } from "../answer/NiraHistory"
import "./matters.css"

// A card that opens is a reading that is ready; only the states that are not say so.
const STANDS: Record<string, string> = { running: "Being read", done: "", failed: "Reading did not finish" }

/** Days from today to an ISO date, counted on the reader's calendar. */
export function daysTo(iso: string, now = new Date()): number | null {
  const [y, m, d] = iso.split("-").map(Number)
  if (!y || !m || !d) return null
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.round((new Date(y, m - 1, d).getTime() - today) / 864e5)
}

/**
 * The date that matters next, said with how far off it is. Past due is a fact, not an emergency:
 * it reads in plain ink with how long ago. Only a reply inside 7 days is marked urgent.
 */
export function dueLine(m: CaseFileListRow, now = new Date()): { text: string; urgent: boolean } | null {
  const due = m.facts?.reply_due
  if (!due) return null
  const left = daysTo(due, now)
  if (left === null) return null
  if (left < 0) return { text: `Reply was due ${shortDate(due)} · ${-left} ${left === -1 ? "day" : "days"} ago`, urgent: false }
  if (left === 0) return { text: "Reply due today", urgent: true }
  return { text: `Reply by ${shortDate(due)} · ${left} ${left === 1 ? "day" : "days"} left`, urgent: left <= 7 }
}

/** One matter, as a card: what it is, then one line of three facts: deadline, amount, section. */
export function MatterCard({ m }: { m: CaseFileListRow }) {
  const due = dueLine(m)
  const reading = m.latest_reading
  // A card that opens is a reading that is ready; only a state that is not says so.
  const stands = reading ? STANDS[reading.status] ?? "" : m.documents ? "Not read yet" : "No document yet"
  const rest = [m.facts?.proposed_total ? `${lakh(m.facts.proposed_total)} proposed` : "", m.facts?.section ? `s.${m.facts.section}` : ""].filter(Boolean)
  return (
    <Link className="nmt-card" to={matterPath(m)}>
      <span className="nmt-k">{isAppeal(m) ? "Appeal or not" : "Notice"}{m.facts?.state ? ` · ${m.facts.state}` : ""}</span>
      <span className="nmt-t">{m.title}</span>
      <span className="nmt-f">
        {due ? <span className={`nmt-due${due.urgent ? " urgent" : ""}`}>{due.text}</span> : null}
        {rest.length ? `${due ? " · " : ""}${rest.join(" · ")}` : null}
      </span>
      {stands ? <span className="nmt-s">{stands}</span> : null}
      <span className="nmt-s">{`${m.threads} ${m.threads === 1 ? "session" : "sessions"} · ${m.documents} ${m.documents === 1 ? "file" : "files"} · updated ${shortDate(m.updated_at)}`}</span>
    </Link>
  )
}

/** Matters as a workspace: one group per door, each a grid of cards. */
export function MatterGroups({ rows }: { rows: CaseFileListRow[] }) {
  const notices = rows.filter((m) => !isAppeal(m))
  const appeals = rows.filter(isAppeal)
  return (
    <>
      {[["Notices", notices], ["Appeal or not", appeals]].map(([title, list]) =>
        (list as CaseFileListRow[]).length ? (
          <section className="nmt-group" key={title as string} aria-label={title as string}>
            <h2 className="ncf-eyebrow">{title as string} · {(list as CaseFileListRow[]).length}</h2>
            <div className="nmt-grid">{(list as CaseFileListRow[]).map((m) => <MatterCard key={m.id} m={m} />)}</div>
          </section>
        ) : null,
      )}
    </>
  )
}
